import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

/**
 * Account deletion page for App Store / Play “delete account” / privacy choices URLs.
 * Paths: /delete-account and /fieldapp/delete-account
 */
const DeleteAccountPage: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onDelete = async () => {
    setError(null);
    if (confirmText.trim().toUpperCase() !== 'DELETE') {
      setError('Type DELETE to confirm.');
      return;
    }
    setBusy(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('delete-account', {
        body: { confirm: true },
      });
      if (fnError) {
        let message = fnError.message || 'Could not delete account.';
        const response = (fnError as { context?: unknown }).context;
        if (response instanceof Response) {
          const body = await response.json().catch(() => null);
          if (body?.error) message = String(body.error);
        }
        setError(message);
        return;
      }
      if (data?.error) {
        setError(String(data.error));
        setBusy(false);
        return;
      }
      await signOut();
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete account.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="container my-5">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-lg-7">
            <h1 className="mb-3">Account deleted</h1>
            <p>
              Your Outreach Bible Project account and associated personal data have been removed,
              except records we may be required to keep for legal or safety reasons.
            </p>
            <p>
              <Link to="/">Return home</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-lg-7">
            <h1 className="mb-3">Delete your account</h1>
            <p>
              Sign in with the same account you use on theobp.com or in the OBP Field app, then you
              can permanently delete your account from this page.
            </p>
            <Link
              className="btn btn-primary"
              to={`/login?redirect=${encodeURIComponent('/delete-account')}`}
            >
              Sign in to continue
            </Link>
            <p className="mt-4 small text-muted">
              Prefer email? Contact{' '}
              <a href="mailto:David_OBP@outlook.com">David_OBP@outlook.com</a> with the subject “Account
              deletion request” and the email on your account. Self-serve deletion on this page is
              the fastest path.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-lg-7">
          <h1 className="mb-3">Delete your account</h1>
          <p>
            Signed in as <strong>{user.email}</strong>.
          </p>
          <div className="alert alert-warning" role="alert">
            <strong>This cannot be undone.</strong> Deleting your account removes your login,
            attendee profile, applications tied to your profile, field push tokens, and related
            personal data we are not required to retain. Group chat history you authored may also be
            removed. Portal admin accounts cannot be deleted here.
          </div>
          <p>Type <code>DELETE</code> to confirm:</p>
          <input
            className="form-control mb-3"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            disabled={busy}
            aria-label="Type DELETE to confirm"
          />
          {error ? (
            <p className="text-danger" role="alert">
              {error}
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn-danger me-2"
            onClick={onDelete}
            disabled={busy || confirmText.trim().toUpperCase() !== 'DELETE'}
          >
            {busy ? 'Deleting…' : 'Permanently delete my account'}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navigate('/dashboard')}
            disabled={busy}
          >
            Cancel
          </button>
          <p className="mt-4">
            <Link to="/privacy">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
